import type { Tutorial } from '../types'

const IMG = '/images/tutorials/mobile/accountability-partners'

export const accountabilityPartnersTutorial: Tutorial = {
  slug: 'accountability-partners',
  title: 'Goals Partners',
  subtitle: 'Goal-scoped support without exposing your full profile',
  date: '2026-07-09',
  category: 'Sharing & Support',
  platform: 'mobile',
  tags: ['Goals Partners', 'invite', 'goals', 'partnerships'],
  description:
    'Bring someone into a single goal — tightly scoped, not a full profile share — so you have real support behind a habit you\'re trying to build.',
  highlights: [
    'Invite from the goal itself, with plain-language boundaries on what partners can see.',
    'Partners see only that one goal and its check-ins — no genetics, no health records.',
    'Partnerships hub is role-aware: needs attention, people you support, people supporting you.',
  ],
  coverImage: `${IMG}/01_invite_explainer.png`,
  sections: [
    {
      heading: 'Inviting from a goal',
      paragraphs: [
        'The entry point lives on the goal itself, not in a separate "add a partner" flow. Livewello explains what a Goals Partner is and, more importantly, exactly what they can and can\'t see before asking for an email.',
      ],
      bullets: [
        '**What they can see** — only this one goal and its check-ins. No health records, no genetic data, nothing else on the profile.',
        '**How it works** — an email invite, acceptance is required before they see anything, and the owner can remove them at any time.',
      ],
      image: {
        src: `${IMG}/01_invite_explainer.png`,
        alt: 'Goals Partner invite explainer on a wellness goal',
        caption: 'Goals Partner explainer',
      },
    },
    {
      heading: 'Sending the invite',
      paragraphs: [
        'Tapping **Choose a partner** reveals the form — an email address and an optional personal note. **Send invite** fires the invite off; the recipient gets an email and becomes a Goals Partner once they accept. Nothing is visible to them before that.',
      ],
      image: {
        src: `${IMG}/02_invite_form.png`,
        alt: 'Goals Partner invite form with email field',
        caption: 'Invite form',
      },
    },
    {
      heading: 'Managing partnerships',
      paragraphs: [
        'The Partnerships hub is the delivery room for every active relationship, whether the invite started on mobile or web. The list is role-aware:',
      ],
      bullets: [
        '**Needs attention** — structured signals when someone you\'re supporting went quiet or hit a streak milestone.',
        '**People I\'m supporting** — goal owners you support, with suggested next actions.',
        '**People supporting me** — Goals Partners you\'ve invited onto your goals.',
      ],
      image: {
        src: `${IMG}/03_partners_list.png`,
        alt: 'Partnerships list with role-aware sections',
        caption: 'Partners list',
      },
    },
  ],
  whyMatters:
    'The scoping is the whole point. Someone willing to share "I\'m trying to build a sleep routine" with a friend is very often not willing to share their full genetic report or every health record — and shouldn\'t have to choose between full exposure and no support at all.',
  nextSlug: 'coach-copilot',
  relatedSlugs: ['coach-copilot', 'collaborator-messaging', 'profile-sharing'],
  meta: {
    description:
      'How Goals Partners work: goal-scoped invites, plain-language boundaries, and a partnerships hub for both sides.',
    ogImage: `${IMG}/01_invite_explainer.png`,
  },
}
